import Uppy, { UppyFile } from '@uppy/core';
import {
  ContentLibraryFileUploadAcceptedPayload,
  ContentLibraryType,
} from 'features/content-library/types';
import { notification } from 'features/notifications/toast/notification';
import i18n from 'i18n';
import axios from 'axios';
import { FileUploader } from 'utils/upload/FileUploader';
import { UploadParams } from 'utils/upload/types';
import { store } from 'store/store';
import {
  fileUploadFailed,
  fileUploadProgressChanged,
} from 'features/content-library/contentLibrarySlice';
import { isError, isUppyRestrictionError } from 'utils/types';
import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { requestLibraryFileUpload } from 'utils/content-library/services';
import {
  ALLOWED_FILE_TYPES,
  BYTES_IN_MB,
  MAX_AUDIO_FILE_SIZE_IN_MB,
  MAX_FILE_SIZE_IN_MB,
  MAX_IMAGE_FILE_SIZE_IN_MB,
  MAX_VIDEO_FILE_SIZE_IN_MB,
} from 'utils/content-library/constants';

const LANGUAGE_TO_UPPY_LOCALE: Record<string, string> = {
  en: 'en_US',
  df: 'de_DE',
  'ach-UG': 'en_US',
};

async function loadUppyLocale(language: string) {
  const localeName = LANGUAGE_TO_UPPY_LOCALE[language] ?? language.replace('-', '_');
  switch (localeName) {
    case 'ar_SA':
      return (await import('@uppy/locales/lib/ar_SA')).default;
    case 'bg_BG':
      return (await import('@uppy/locales/lib/bg_BG')).default;
    case 'ca_ES':
      return (await import('@uppy/locales/lib/ca_ES')).default;
    case 'cs_CZ':
      return (await import('@uppy/locales/lib/cs_CZ')).default;
    case 'da_DK':
      return (await import('@uppy/locales/lib/da_DK')).default;
    case 'de_DE':
      return (await import('@uppy/locales/lib/de_DE')).default;
    case 'fi_FI':
      return (await import('@uppy/locales/lib/fi_FI')).default;
    case 'es_ES':
      return (await import('@uppy/locales/lib/es_ES')).default;
    case 'fr_FR':
      return (await import('@uppy/locales/lib/fr_FR')).default;
    case 'it_IT':
      return (await import('@uppy/locales/lib/it_IT')).default;
    case 'nb_NO':
      return (await import('@uppy/locales/lib/nb_NO')).default;
    case 'nl_NL':
      return (await import('@uppy/locales/lib/nl_NL')).default;
    case 'pt_PT':
      return (await import('@uppy/locales/lib/pt_PT')).default;
    case 'ro_RO':
      return (await import('@uppy/locales/lib/ro_RO')).default;
    case 'ru_RU':
      return (await import('@uppy/locales/lib/ru_RU')).default;
    case 'sv_SE':
      return (await import('@uppy/locales/lib/sv_SE')).default;
    case 'tr_TR':
      return (await import('@uppy/locales/lib/tr_TR')).default;
    case 'zh_CN':
      return (await import('@uppy/locales/lib/zh_CN')).default;
    case 'zh_TW':
      return (await import('@uppy/locales/lib/zh_TW')).default;
    default:
      return (await import('@uppy/locales/lib/en_US')).default;
  }
}

interface UploadFileParams {
  activeFolderId: string;
  activeLibraryType: ContentLibraryType;
}

type UppyFileMeta = {
  library_id: string;
  expiration: number;
  file_id: string;
  file_name: string;
};

export class Manager {
  private uppy?: Uppy<UppyFileMeta, Record<string, any>>;

  private internalIdByFileId = new Map<string, string>();

  private fileUploader = new FileUploader();

  private uploadParams = new Map<string, ContentLibraryFileUploadAcceptedPayload>();

  initialize = (language?: string) => {
    this.uppy = new Uppy<UppyFileMeta, Record<string, any>>({
      restrictions: {
        maxFileSize: MAX_FILE_SIZE_IN_MB * BYTES_IN_MB,
        allowedFileTypes: ALLOWED_FILE_TYPES,
      },
      onBeforeFileAdded: () => true,
    });

    if (language) {
      this.setLocale(language);
    }

    return this.uppy;
  };

  setLocale = async (language: string) => {
    try {
      const locale = await loadUppyLocale(language);
      this.getUppy().setOptions({ locale });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  upload = async (files: FileList | null, params: UploadFileParams) => {
    if (!files) {
      return;
    }

    const uploadPromises = Array.from(files).map((file) => this.prepareFileUpload(file, params));
    await Promise.allSettled(uploadPromises);

    const uppy = this.getUppy();
    const uppyFiles = uppy.getFiles().filter((f) => !f.error);

    if (!uppyFiles.length) {
      return;
    }

    uppy.emit('upload-start', uppyFiles);

    await Promise.allSettled(uppyFiles.map((uppyFile) => this.uploadFile(uppyFile)));

    uppy.clear();
    this.internalIdByFileId.clear();
    this.uploadParams.clear();
    this.fileUploader.cancelAll();
  };

  cancelUpload = (fileId: string) => {
    this.fileUploader.cancel(fileId);

    const internalFileId = this.internalIdByFileId.get(fileId);
    if (internalFileId) {
      const uppy = this.getUppy();
      uppy.removeFile(internalFileId);
    }
  };

  private getUppy() {
    if (!this.uppy) {
      return this.initialize();
    }

    return this.uppy;
  }

  private prepareFileUpload = async (file: File, params: UploadFileParams) => {
    let fileId = '';
    let serverFileId = '';

    const uppy = this.getUppy();

    try {
      const isVideo = file.type?.startsWith('video/');
      const isAudio = file.type?.startsWith('audio/');
      const isImage = file.type?.startsWith('image/');

      if (isVideo) {
        uppy.setOptions({
          restrictions: {
            ...uppy.opts.restrictions,
            maxFileSize: MAX_VIDEO_FILE_SIZE_IN_MB * BYTES_IN_MB,
          },
        });
      } else if (isAudio) {
        uppy.setOptions({
          restrictions: {
            ...uppy.opts.restrictions,
            maxFileSize: MAX_AUDIO_FILE_SIZE_IN_MB * BYTES_IN_MB,
          },
        });
      } else if (isImage) {
        uppy.setOptions({
          restrictions: {
            ...uppy.opts.restrictions,
            maxFileSize: MAX_IMAGE_FILE_SIZE_IN_MB * BYTES_IN_MB,
          },
        });
      }

      try {
        fileId = uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
        });
      } finally {
        if (isVideo || isAudio || isImage) {
          uppy.setOptions({
            restrictions: {
              ...uppy.opts.restrictions,
              maxFileSize: MAX_FILE_SIZE_IN_MB * BYTES_IN_MB,
            },
          });
        }
      }

      const uploadAccepted = await requestLibraryFileUpload({
        activeLibraryType: params.activeLibraryType,
        fileName: file.name,
        folderId: params.activeFolderId,
        fileSize: file.size,
      });

      // file may have been removed while awaiting the signaling response
      // (e.g. user cancelled, or a previous batch cleared the uppy instance)
      if (!uppy.getFile(fileId)) {
        return;
      }

      serverFileId = uploadAccepted.id;
      this.uploadParams.set(serverFileId, uploadAccepted);
      this.internalIdByFileId.set(serverFileId, fileId);

      // set required upload meta
      uppy.setFileMeta(fileId, {
        library_id: uploadAccepted.libraryId,
        expiration: uploadAccepted.expirationTimestamp,
        file_id: uploadAccepted.id,
        file_name: uploadAccepted.storageFilename,
      });
    } catch (error) {
      uppy.removeFile(fileId);
      if (serverFileId) {
        this.internalIdByFileId.delete(serverFileId);
        this.uploadParams.delete(serverFileId);
      }

      this.handlePrepareFileUploadError(error, file.name);

      throw error;
    }
  };

  private toUploadParams = (accepted: ContentLibraryFileUploadAcceptedPayload): UploadParams => {
    if ('parts' in accepted) {
      return {
        strategy: 'multipart',
        callbackUrl: accepted.callbackUrl,
        callbackToken: accepted.callbackToken,
        uploadId: accepted.uploadId,
        partSize: accepted.partSize,
        parts: accepted.parts,
      };
    }

    if ('callbackUrl' in accepted) {
      return {
        strategy: 'presigned',
        storageUrl: accepted.storageUrl,
        callbackUrl: accepted.callbackUrl,
        callbackToken: accepted.callbackToken,
        callbackBody: {
          library_id: accepted.libraryId,
          expiration: accepted.expirationTimestamp,
          file_id: accepted.id,
          file_name: accepted.storageFilename,
        },
      };
    }

    return {
      strategy: 'legacy',
      storageUrl: accepted.storageUrl,
      token: accepted.token,
      formFields: {
        library_id: accepted.libraryId,
        expiration: String(accepted.expirationTimestamp),
        file_id: accepted.id,
        file_name: accepted.storageFilename,
      },
    };
  };

  private uploadFile = async (uppyFile: UppyFile<UppyFileMeta, Record<string, any>>) => {
    const serverFileId = uppyFile.meta.file_id;
    const uppyFileId = uppyFile.id;

    if (!(uppyFile.data instanceof Blob)) {
      throw new Error(`No file data for ${serverFileId}`);
    }

    // Correct blob MIME type (same as Uppy's XHRUpload plugin's setTypeInBlob)
    const blob = uppyFile.data.slice(0, uppyFile.data.size, uppyFile.meta.type);

    const fileParams = this.uploadParams.get(serverFileId);
    if (!fileParams) {
      throw new Error(`Upload params not found for file ${serverFileId}`);
    }

    try {
      await this.fileUploader.upload(serverFileId, blob, this.toUploadParams(fileParams), {
        fileName: uppyFile.meta.name ?? uppyFile.name,
        onProgress: (loaded, total) => {
          const uppy = this.getUppy();
          const file = uppy.getFile(uppyFileId);

          if (file) {
            uppy.emit('upload-progress', file, {
              uploadStarted: file.progress.uploadStarted ?? 0,
              bytesUploaded: loaded,
              bytesTotal: total,
            });
          }

          const percentage = (loaded / total) * 100;
          store.dispatch(
            fileUploadProgressChanged({
              id: serverFileId,
              progress: parseFloat(percentage.toFixed(2)),
            })
          );
        },
      });

      const uppy = this.getUppy();
      const file = uppy.getFile(uppyFileId);
      if (file) {
        uppy.emit('upload-success', file, { status: 200, body: {} });
      }
    } catch (error) {
      if (axios.isCancel(error) || (error instanceof Error && error.name === 'AbortError')) {
        return;
      }

      Sentry.captureException(error);
      logger.remote().error(`Cannot upload file=${uppyFile.name}`, error);

      const uppy = this.getUppy();
      const file = uppy.getFile(uppyFileId);
      if (file) {
        uppy.emit(
          'upload-error',
          file,
          error instanceof Error ? error : new Error('Upload failed')
        );
      }
      store.dispatch(fileUploadFailed({ id: serverFileId }));
      notification(
        i18n.t('notifications:content_library.file_upload_error', {
          fileName: uppyFile.name,
        })
      );
    }
  };

  private handlePrepareFileUploadError(error: unknown, fileName: string) {
    if (isUppyRestrictionError(error)) {
      notification(error.message, { autoClose: 5000 });
    } else {
      Sentry.captureException(error);
      logger.remote().error(`Cannot upload a file=${fileName}`, error);
      if (isError(error)) {
        notification(
          i18n.t('notifications:content_library.file_upload_general_error', {
            fileName,
          }),
          { autoClose: 5000 }
        );
      } else {
        notification(i18n.t('notifications:content_library.file_upload_general_error'));
      }
    }
  }
}

export const ContentLibraryManager = new Manager();
